import { useState, useEffect } from "react";
import { toast } from "sonner";
import apiClient from "../services/apiClient";
import { Button } from "./ui/button";
import { 
  Mail, 
  Server, 
  Shield, 
  Eye, 
  EyeOff,
  CheckCircle,
  AlertCircle,
  Settings
} from "lucide-react";

export const SMTPConfig = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    smtp_server: '',
    smtp_port: 587,
    use_tls: true,
    smtp_user: '',
    smtp_password: '',
    from_name: 'Mindful Wellness'
  });

  useEffect(() => {
    fetchSMTPConfig();
  }, []);

  const fetchSMTPConfig = async () => {
    try {
      const response = await apiClient.get('/admin/smtp-config');
      setConfig(response.data);
      setFormData({
        smtp_server: response.data.smtp_server,
        smtp_port: response.data.smtp_port,
        use_tls: response.data.use_tls,
        smtp_user: response.data.smtp_user,
        smtp_password: response.data.smtp_password,
        from_name: response.data.from_name
      });
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error('Failed to fetch SMTP config:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (config) {
        // Update existing config
        await apiClient.put(`/admin/smtp-config/${config.id}`, formData);
        toast.success('SMTP-Konfiguration aktualisiert');
      } else {
        // Create new config
        await apiClient.post('/admin/smtp-config', formData);
        toast.success('SMTP-Konfiguration erstellt');
      }
      
      fetchSMTPConfig();
    } catch (error) {
      toast.error('Fehler beim Speichern der SMTP-Konfiguration');
      console.error('SMTP config error:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleTestSMTP = async () => {
    setTesting(true);
    
    try {
      const response = await apiClient.post('/admin/test-smtp');
      if (response.data.success) {
        toast.success('Test-E-Mail erfolgreich gesendet!');
      } else {
        toast.error('Test-E-Mail konnte nicht gesendet werden');
      }
    } catch (error) {
      toast.error('SMTP-Test fehlgeschlagen');
      console.error('SMTP test error:', error);
    } finally {
      setTesting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl sm:text-2xl font-semibold text-charcoal-800 font-display">
          SMTP E-Mail Konfiguration
        </h2>
        {config && (
          <div className="flex items-center space-x-2">
            <CheckCircle className="w-5 h-5 text-green-500" />
            <span className="text-sm text-green-600">Konfiguriert</span>
          </div>
        )}
      </div>

      <div className="card p-6">
        <div className="flex items-center space-x-3 mb-6">
          <Mail className="w-6 h-6 text-sage-600" />
          <div>
            <h3 className="text-lg font-semibold text-charcoal-800">E-Mail Server Einstellungen</h3>
            <p className="text-sm text-charcoal-600">
              Konfigurieren Sie SMTP für den Versand von Passwort-Reset E-Mails
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* SMTP Server */}
            <div>
              <label className="block text-charcoal-700 text-sm font-medium mb-2">
                <Server className="w-4 h-4 inline mr-2" />
                SMTP Server
              </label>
              <input
                type="text"
                name="smtp_server"
                value={formData.smtp_server}
                onChange={handleInputChange}
                className="input-field"
                placeholder="smtp.deinedomain.de"
                required
              />
            </div>

            {/* Port */}
            <div>
              <label className="block text-charcoal-700 text-sm font-medium mb-2">
                Port
              </label>
              <input
                type="number"
                name="smtp_port"
                value={formData.smtp_port}
                onChange={handleInputChange}
                className="input-field"
                placeholder="587"
                min="1"
                max="65535"
                required
              />
            </div>
          </div>

          {/* TLS */}
          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              id="use_tls"
              name="use_tls"
              checked={formData.use_tls}
              onChange={handleInputChange}
              className="rounded border-sage-300"
            />
            <label htmlFor="use_tls" className="text-charcoal-700 text-sm font-medium flex items-center">
              <Shield className="w-4 h-4 mr-2" />
              TLS verwenden (empfohlen)
            </label>
          </div>

          {/* Username */}
          <div>
            <label className="block text-charcoal-700 text-sm font-medium mb-2">
              Benutzername / E-Mail
            </label>
            <input
              type="email"
              name="smtp_user"
              value={formData.smtp_user}
              onChange={handleInputChange}
              className="input-field"
              placeholder="no-reply@deinedomain.de"
              required
            />
          </div>

          {/* Password */}
          <div>
            <label className="block text-charcoal-700 text-sm font-medium mb-2">
              Passwort
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                name="smtp_password"
                value={formData.smtp_password}
                onChange={handleInputChange}
                className="input-field pr-12"
                placeholder="********"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-charcoal-500 hover:text-sage-600 transition-colors"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          {/* From Name */}
          <div>
            <label className="block text-charcoal-700 text-sm font-medium mb-2">
              Absendername
            </label>
            <input
              type="text"
              name="from_name"
              value={formData.from_name}
              onChange={handleInputChange}
              className="input-field"
              placeholder="Mindful Wellness"
              required
            />
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4 pt-4">
            <Button
              type="submit"
              disabled={saving}
              className="flex-1 bg-sage-500 hover:bg-sage-600 text-white"
            >
              {saving ? (
                <>
                  <div className="spinner mr-2"></div>
                  Speichern...
                </>
              ) : (
                <>
                  <Settings className="w-4 h-4 mr-2" />
                  {config ? 'Aktualisieren' : 'Konfiguration speichern'}
                </>
              )}
            </Button>

            {config && (
              <Button
                type="button"
                onClick={handleTestSMTP}
                disabled={testing}
                variant="outline"
                className="flex-1 text-blue-600 border-blue-200 hover:bg-blue-50"
              >
                {testing ? (
                  <>
                    <div className="spinner mr-2"></div>
                    Teste...
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4 mr-2" />
                    Test-E-Mail senden
                  </>
                )}
              </Button>
            )}
          </div>
        </form>

        {/* Info Box */}
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start space-x-3">
            <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
            <div className="text-sm">
              <p className="text-blue-800 font-medium mb-1">SMTP Einstellungen</p>
              <ul className="text-blue-700 space-y-1">
                <li>• Port 587 mit TLS ist die empfohlene Konfiguration</li>
                <li>• Die Test-E-Mail wird an Ihre Admin-E-Mail-Adresse gesendet</li>
                <li>• Passwort-Reset E-Mails verwenden diese Konfiguration</li>
                <li>• Stellen Sie sicher, dass Ihr E-Mail-Provider SMTP erlaubt</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};